import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { Container, Input, FormGroup } from "reactstrap";
import { Form, Field } from "react-final-form";
import axios from "axios";
import { connect } from "react-redux";
import { useDispatch, flashSuccess, flash422 } from "@/shared/components/flash";
import {
  makeLabelizedOptionsFromTree,
  labelizeNamedData,
  unpatchSelect,
} from "@/shared/helpers";
import { getProgramTree } from "@/services/program/getProgramTree";
import TemplateButton from "@/shared/components/TemplateButton";
import getUnitNumbers from "@/services/program/getUnitNumbers";

const findProgramOption = (options, value) =>
  options.find((option) => String(option.value) === String(value)) || null;

const stripProgramPrefix = (label) => label?.replace(/^[-\s]+/, "").trim() || "";

const InviteParticipant = ({ organization, rootProgram }) => {
  const [programOptions, setProgramOptions] = useState([]);
  const [program, setProgram] = useState(null);
  const [initialValues, setInitialValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingPrograms, setLoadingPrograms] = useState(true);
  const [unitNumberOptions, setUnitNumberOptions] = useState([]);
  const [submissionMessage, setSubmissionMessage] = useState("");
  const dispatch = useDispatch();

  useEffect(() => {
    let isMounted = true;

    if (!rootProgram?.id) {
      setLoadingPrograms(false);
      return undefined;
    }

    setLoadingPrograms(true);

    getProgramTree(rootProgram.organization_id, rootProgram.id)
      .then((programTree) => {
        if (!isMounted) return;

        const nextOptions = makeLabelizedOptionsFromTree(programTree);
        const defaultProgram =
          findProgramOption(nextOptions, program?.value) ||
          findProgramOption(nextOptions, rootProgram.id) ||
          nextOptions[0] ||
          null;

        setProgramOptions(nextOptions);
        setProgram(defaultProgram);
        setInitialValues({ program: defaultProgram });
        setLoadingPrograms(false);
      })
      .catch(() => {
        if (!isMounted) return;
        setProgramOptions([]);
        setLoadingPrograms(false);
      });

    return () => {
      isMounted = false;
    };
  }, [rootProgram]);

  const selectedProgramId = program?.value ?? rootProgram?.id ?? null;

  useEffect(() => {
    if (!organization?.id || !selectedProgramId) {
      setUnitNumberOptions([]);
      return;
    }

    getUnitNumbers(organization.id, selectedProgramId, "assignable=1")
      .then((response) => {
        setUnitNumberOptions(labelizeNamedData(response));
      })
      .catch(() => {
        setUnitNumberOptions([]);
      });
  }, [organization, selectedProgramId]);

  const handleProgramSelect = (selectedOption, input, form) => {
    setProgram(selectedOption);
    setSubmissionMessage("");
    input.onChange(selectedOption);
    form.change("unit_number", null);
  };

  const selectedProgramLabel = useMemo(() => {
    if (program?.label) {
      return stripProgramPrefix(program.label);
    }

    return rootProgram?.name || "No program selected";
  }, [program, rootProgram]);

  const selectedProgramMeta = useMemo(() => {
    if (unitNumberOptions.length > 0) {
      return `${unitNumberOptions.length.toLocaleString()} unit numbers available`;
    }

    return "";
  }, [unitNumberOptions]);

  const onSubmit = async (values, form) => {
    const selectedProgram = values.program || program;

    if (!selectedProgram?.value) {
      return;
    }

    setLoading(true);
    setSubmissionMessage("");

    let normalizedValues = { ...values };
    if (typeof normalizedValues.unit_number === "object") {
      normalizedValues = unpatchSelect(normalizedValues, ["unit_number"]);
    }

    const payload = {
      first_name: normalizedValues.first_name,
      last_name: normalizedValues.last_name,
      email: normalizedValues.email,
      external_id: normalizedValues.external_id || null,
      unit_number: normalizedValues.unit_number || null,
    };

    try {
      const response = await axios.put(
        `/organization/${organization.id}/program/${selectedProgram.value}/invite`,
        payload
      );

      if (response.status === 200) {
        flashSuccess(dispatch, "The user has been invited to your program!");
        setSubmissionMessage(`Invitation sent to ${payload.email}.`);
        setProgram(selectedProgram);
        form.restart({ program: selectedProgram });
      }
    } catch (error) {
      flash422(dispatch, error?.response?.data || error);
    } finally {
      setLoading(false);
    }
  };

  if (!organization?.id || !rootProgram?.id) {
    return (
      <div className="manager-home manager-invite-page">
        <Container className="manager-home__shell manager-home__content">
          <div className="manager-card manager-card--loading">
            Loading invite workspace...
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="manager-home manager-invite-page">
      <Container className="manager-home__shell manager-home__content">
        <div className="manager-invite">
          <section className="manager-card manager-invite__hero">
            <div className="manager-invite__hero-copy">
              <span className="manager-card__eyebrow">Invite</span>
              <h1 className="manager-invite__title">Invite User</h1>
              <p className="manager-invite__description">
                Send a user invitation into a reward program.
              </p>
            </div>
          </section>

          <div className="manager-invite__layout">
            <article className="manager-card manager-invite__panel">
              <div className="manager-invite__panel-head">
                <div>
                  <span className="manager-card__eyebrow">Invite</span>
                  <h2 className="manager-card__title">Invitation Details</h2>
                </div>
              </div>
              <p className="manager-invite__panel-description">
                Select a program, enter the user details, and send the invite.
              </p>

              <div className="manager-invite__panel-body">
                {submissionMessage && (
                  <div
                    className="manager-invite__status is-success"
                    role="status"
                    aria-live="polite"
                  >
                    <strong>Invite sent</strong>
                    <span>{submissionMessage}</span>
                  </div>
                )}

                {loadingPrograms && (
                  <div className="manager-invite__empty">
                    Loading available programs...
                  </div>
                )}

                {!loadingPrograms && programOptions.length === 0 && (
                  <div className="manager-invite__empty">
                    No assignable programs are available for invitations.
                  </div>
                )}

                {!loadingPrograms && programOptions.length > 0 && (
                  <Form
                    onSubmit={onSubmit}
                    initialValues={initialValues}
                    keepDirtyOnReinitialize
                    validate={validate}
                  >
                    {({ handleSubmit, form }) => (
                      <form className="manager-invite__form" onSubmit={handleSubmit}>
                        <div className="manager-invite__summary">
                          <div className="manager-invite__summary-main">
                            <span className="manager-card__eyebrow">Selected Program</span>
                            <strong className="manager-invite__summary-value">
                              {selectedProgramLabel}
                            </strong>
                          </div>
                          {selectedProgramMeta && (
                            <span className="manager-invite__summary-meta">
                              {selectedProgramMeta}
                            </span>
                          )}
                        </div>

                        <section className="manager-invite__section">
                          <div className="manager-invite__section-head">
                            <span className="manager-card__eyebrow">Program</span>
                            <h3 className="manager-card__title">Assignment</h3>
                          </div>

                          <div className="manager-invite__grid manager-invite__grid--stacked">
                            <Field name="program">
                              {({ input, meta }) => (
                                <div className="manager-invite__field manager-invite__field--full">
                                  <label className="manager-invite__field-label">
                                    Reward Program
                                    <span className="manager-invite__required">*</span>
                                  </label>
                                  <Select
                                    options={programOptions}
                                    isClearable={false}
                                    isLoading={loadingPrograms}
                                    className="react-select"
                                    classNamePrefix="react-select"
                                    placeholder="Select a reward program"
                                    value={input.value || program}
                                    onChange={(selectedOption) =>
                                      handleProgramSelect(selectedOption, input, form)
                                    }
                                    onBlur={() => input.onBlur(input.value)}
                                  />
                                  {meta.touched && meta.error && (
                                    <span className="form-error">{meta.error}</span>
                                  )}
                                </div>
                              )}
                            </Field>

                            {unitNumberOptions.length > 0 && (
                              <Field name="unit_number">
                                {({ input, meta }) => (
                                  <div className="manager-invite__field">
                                    <label className="manager-invite__field-label">
                                      Unit Number
                                    </label>
                                    <Select
                                      options={unitNumberOptions}
                                      isClearable
                                      className="react-select"
                                      classNamePrefix="react-select"
                                      placeholder="Optional unit assignment"
                                      value={input.value || null}
                                      onChange={input.onChange}
                                      onBlur={() => input.onBlur(input.value)}
                                    />
                                    {meta.touched && meta.error && (
                                      <span className="form-error">{meta.error}</span>
                                    )}
                                  </div>
                                )}
                              </Field>
                            )}
                          </div>
                        </section>

                        <section className="manager-invite__section">
                          <div className="manager-invite__section-head">
                            <span className="manager-card__eyebrow">User</span>
                            <h3 className="manager-card__title">User Details</h3>
                          </div>

                          <div className="manager-invite__grid">
                            <Field name="first_name">
                              {({ input, meta }) => (
                                <div className="manager-invite__field">
                                  <label className="manager-invite__field-label">
                                    First Name
                                    <span className="manager-invite__required">*</span>
                                  </label>
                                  <FormGroup>
                                    <Input
                                      className="manager-invite__input"
                                      placeholder="Enter first name"
                                      type="text"
                                      {...input}
                                    />
                                  </FormGroup>
                                  {meta.touched && meta.error && (
                                    <span className="form-error">{meta.error}</span>
                                  )}
                                </div>
                              )}
                            </Field>

                            <Field name="last_name">
                              {({ input, meta }) => (
                                <div className="manager-invite__field">
                                  <label className="manager-invite__field-label">
                                    Last Name
                                    <span className="manager-invite__required">*</span>
                                  </label>
                                  <FormGroup>
                                    <Input
                                      className="manager-invite__input"
                                      placeholder="Enter last name"
                                      type="text"
                                      {...input}
                                    />
                                  </FormGroup>
                                  {meta.touched && meta.error && (
                                    <span className="form-error">{meta.error}</span>
                                  )}
                                </div>
                              )}
                            </Field>

                            <Field name="email">
                              {({ input, meta }) => (
                                <div className="manager-invite__field manager-invite__field--full">
                                  <label className="manager-invite__field-label">
                                    Email Address
                                    <span className="manager-invite__required">*</span>
                                  </label>
                                  <FormGroup>
                                    <Input
                                      className="manager-invite__input"
                                      placeholder="name@company.com"
                                      type="email"
                                      {...input}
                                    />
                                  </FormGroup>
                                  {meta.touched && meta.error && (
                                    <span className="form-error">{meta.error}</span>
                                  )}
                                </div>
                              )}
                            </Field>

                            <Field name="external_id">
                              {({ input, meta }) => (
                                <div className="manager-invite__field manager-invite__field--full">
                                  <label className="manager-invite__field-label">
                                    External ID
                                  </label>
                                  <FormGroup>
                                    <Input
                                      className="manager-invite__input"
                                      placeholder="Optional employee or participant ID"
                                      type="text"
                                      {...input}
                                    />
                                  </FormGroup>
                                  {meta.touched && meta.error && (
                                    <span className="form-error">{meta.error}</span>
                                  )}
                                </div>
                              )}
                            </Field>
                          </div>
                        </section>

                        <div className="manager-invite__footer">
                          <p className="manager-invite__footer-copy">
                            Invitation emails are sent immediately.
                          </p>
                          <TemplateButton
                            className="manager-home__send-btn manager-invite__submit"
                            type="submit"
                            text="Invite User"
                            disabled={loading}
                            spinner={loading}
                          />
                        </div>
                      </form>
                    )}
                  </Form>
                )}
              </div>
            </article>
          </div>
        </div>
      </Container>
    </div>
  );
};

const validate = (values) => {
  const errors = {};

  if (!values.program) {
    errors.program = "Select a program";
  }
  if (!values.email) {
    errors.email = "Email is required";
  } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+.[A-Z]{2,}$/i.test(values.email)) {
    errors.email = "Invalid email address";
  }
  if (!values.first_name) {
    errors.first_name = "First name is required";
  }
  if (!values.last_name) {
    errors.last_name = "Last name is required";
  }

  return errors;
};

const mapStateToProps = (state) => {
  return {
    rootProgram: state.rootProgram,
    organization: state.organization,
  };
};

export default connect(mapStateToProps)(InviteParticipant);
